/**
 * Shell navegable del lobby online: entrada → lista de salas → sala.
 *
 * Consume un `RoomListProvider` (hoy el mock, en M1–M3 el real Colyseus/CDN)
 * SIN conocer la implementación. Reutiliza el lenguaje visual del
 * juego con su propio namespace CSS (`.lob-*`). Renderiza todos los
 * `ConnectionState` (§7) para que en M1 solo haya que cambiar la fuente de
 * datos, no la vista.
 */
import {
  RoomListProvider,
  PlayerIdentity,
  RegionId,
  RoomFilter,
  ConnectionState,
  annotatePing,
  normalizeSearch,
  sortRooms,
  RoomSort,
  RoomWithPing,
  RegionPing,
} from "./contract";
import { AVAILABLE_REGIONS } from "./offlineProvider";
import { hasBackend } from "./regions";
import {
  countryToFlag,
  pingTier,
  regionLabel,
  statusLabel,
  makeGuestIdentity,
} from "./format";
import { injectThemeTokens } from "../theme";
import { MAPS, MIN_CAPACITY, MAX_CAPACITY, DEFAULT_CAPACITY, clampCapacity } from "@tak/sim";

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls = "",
  html?: string,
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (html !== undefined) e.innerHTML = html;
  return e;
}

const LOBBY_CSS = `
/* ============================================================================
   LOBBY ONLINE · piel "Tactical Broadcast".
   Solo maqueta: el color, la tipografía y los controles vienen del sistema de
   diseño (src/design/system.ts, clases .tb-*).
   ========================================================================== */
.lob-root{position:fixed;inset:0;z-index:18;display:none;align-items:center;justify-content:center;
  padding:max(var(--sp-4),env(safe-area-inset-top)) max(var(--sp-4),env(safe-area-inset-right))
          max(var(--sp-4),env(safe-area-inset-bottom)) max(var(--sp-4),env(safe-area-inset-left));
  color:var(--tb-text);font-family:var(--tb-font-ui);
  background:radial-gradient(130% 110% at 50% -10%,rgba(18,28,40,0.86),rgba(4,6,10,0.97));
  backdrop-filter:blur(0.5rem);-webkit-backdrop-filter:blur(0.5rem);}
.lob-root.open{display:flex;}
.lob-panel{position:relative;width:min(64rem,100%);height:min(42rem,100%);
  display:flex;flex-direction:column;overflow:hidden;}

/* cabecera: título · identidad · estado de conexión */
.lob-title{font-family:var(--tb-font);font-style:italic;text-transform:uppercase;
  font-size:var(--fs-lg);letter-spacing:0.08em;font-weight:900;line-height:1;}
.lob-title small{display:block;font-family:var(--tb-font-mono);font-style:normal;
  font-size:var(--fs-micro);letter-spacing:0.22em;color:var(--tb-muted);font-weight:600;margin-top:0.35rem;}
.lob-id{margin-left:auto;text-align:right;min-width:0;}
.lob-id b{display:block;font-size:var(--fs-sm);font-weight:800;letter-spacing:0.08em;
  overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.lob-id span{font-family:var(--tb-font-mono);font-size:var(--fs-micro);letter-spacing:0.2em;color:var(--tb-muted);}
.lob-conn{flex:none;font-family:var(--tb-font-mono);font-size:var(--fs-micro);letter-spacing:0.18em;
  font-weight:800;padding:0.4em 0.7em;border:1px solid var(--tb-line);color:var(--tb-muted);
  transform:skewX(var(--tb-skew));}
.lob-conn>span{display:block;transform:skewX(calc(-1 * var(--tb-skew)));}
.lob-conn.idle,.lob-conn.connected{color:var(--tb-cyan);border-color:rgba(34,230,255,0.4);}
.lob-conn.matchmaking,.lob-conn.connecting,.lob-conn.reconnecting{color:var(--tb-warn);border-color:rgba(255,201,60,0.4);}
.lob-conn.region-unavailable,.lob-conn.server-error,.lob-conn.disconnected{color:var(--tb-magenta);border-color:rgba(255,46,99,0.4);}
@media (max-width:479.98px){.lob-conn{display:none;}}

.lob-body{flex:1;padding:var(--sp-5);min-height:0;}
@media (max-width:767.98px){.lob-root{padding:0;}.lob-panel{width:100%;height:100%;}.lob-body{padding:var(--sp-4);}}
@media (max-height:32rem) and (orientation:landscape){.lob-root{padding:0;}.lob-panel{width:100%;height:100%;}}
.lob-foot .tb-btn{flex:1 1 8rem;}

/* ------------------------------- entrada --------------------------------- */
.lob-entry{display:flex;flex-direction:column;align-items:center;justify-content:center;
  height:100%;text-align:center;gap:var(--sp-2);}
.lob-entry .flag{font-size:var(--fs-xl);line-height:1;}
.lob-entry h2{font-family:var(--tb-font);font-style:italic;text-transform:uppercase;
  font-size:var(--fs-xl);letter-spacing:0.06em;font-weight:900;margin:0;}
.lob-entry .sub{font-family:var(--tb-font-mono);font-size:var(--fs-micro);letter-spacing:0.24em;
  color:var(--tb-muted);margin-bottom:var(--sp-5);}
.lob-entry .tb-btn{width:min(22rem,100%);}

/* ------------------------------ herramientas ----------------------------- */
.lob-tools{display:flex;align-items:center;gap:var(--sp-2);margin-bottom:var(--sp-3);flex-wrap:wrap;}
.lob-select{flex:1 1 9rem;min-width:0;}
.lob-chk{display:inline-flex;align-items:center;gap:var(--sp-2);min-height:2.75rem;
  font-size:var(--fs-xs);letter-spacing:0.1em;color:var(--tb-muted);cursor:pointer;text-transform:uppercase;}
.lob-chk input{width:1.1rem;height:1.1rem;accent-color:var(--tb-magenta);}
.lob-count{margin-left:auto;font-family:var(--tb-font-mono);font-size:var(--fs-micro);
  letter-spacing:0.2em;color:var(--tb-muted);}
/* En móvil el contador se lleva su propia línea: en la fila de filtros se
   pisaba con la casilla de "ocultar llenas". */
@media (max-width:767.98px){.lob-count{flex:1 0 100%;text-align:right;margin-left:0;}}
.lob-search{width:100%;margin-bottom:var(--sp-3);}

/* --------------------------- lista de salas ------------------------------ */
/* ESCRITORIO: lista + ficha de la sala a la vez (densidad). MÓVIL: solo lista,
   y la ficha ocupa la pantalla al elegir una. */
.lob-split{display:grid;grid-template-columns:1fr;gap:var(--sp-4);height:100%;min-height:0;}
@media (min-width:1024px){.lob-split.with-detail{grid-template-columns:minmax(0,1.5fr) minmax(0,1fr);}}
.lob-list{display:flex;flex-direction:column;gap:var(--sp-2);min-height:0;}
.lob-row{display:grid;grid-template-columns:1.6rem minmax(0,1fr) auto auto auto auto;align-items:center;
  gap:var(--sp-3);padding:var(--sp-3);background:linear-gradient(158deg,rgba(19,27,36,0.82),rgba(8,12,18,0.82));
  transition:box-shadow .12s ease,transform .12s ease;}
@media (hover:hover) and (pointer:fine){
  .lob-row:hover{box-shadow:inset 0 0 0 1px var(--tb-cyan);transform:translateX(2px);}
}
.lob-row .flag{font-size:var(--fs-md);}
.lob-row .name{display:flex;align-items:center;gap:var(--sp-2);min-width:0;font-size:var(--fs-sm);
  font-weight:800;letter-spacing:0.04em;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.lob-row .name .lock{font-size:var(--fs-xs);color:var(--tb-muted);}
.lob-row .meta{font-family:var(--tb-font-mono);font-size:var(--fs-micro);letter-spacing:0.08em;color:var(--tb-muted);}
.lob-row .st{font-family:var(--tb-font-mono);font-size:var(--fs-micro);letter-spacing:0.12em;font-weight:800;
  padding:0.3em 0.6em;border:1px solid var(--tb-line);color:var(--tb-muted);}
.lob-row .st.waiting{color:var(--tb-cyan);border-color:rgba(34,230,255,0.4);}
.lob-row .st.in-progress{color:var(--tb-warn);border-color:rgba(255,201,60,0.4);}
.lob-row .st.full{color:var(--tb-magenta);border-color:rgba(255,46,99,0.4);}
.lob-ping{display:flex;align-items:center;gap:var(--sp-2);font-family:var(--tb-font-mono);
  font-size:var(--fs-xs);font-weight:800;font-variant-numeric:tabular-nums;}
.lob-ping i{width:0.5rem;height:0.5rem;background:var(--tb-muted);
  clip-path:polygon(50% 0,100% 50%,50% 100%,0 50%);}
.lob-ping.good i{background:var(--tb-cyan);box-shadow:var(--tb-glow-cyan);}
.lob-ping.ok i{background:var(--tb-warn);}
.lob-ping.bad i{background:var(--tb-magenta);}
.lob-join{white-space:nowrap;}
.lob-empty,.lob-loading{text-align:center;color:var(--tb-muted);font-size:var(--fs-xs);
  letter-spacing:0.14em;padding:var(--sp-7) var(--sp-4);line-height:1.7;text-transform:uppercase;}
/* En móvil la fila se parte en dos líneas: nombre arriba, datos y botón abajo. */
@media (max-width:767.98px){
  .lob-row{grid-template-columns:1.6rem minmax(0,1fr) auto;grid-auto-rows:auto;row-gap:var(--sp-2);}
  .lob-row .meta{grid-column:2 / -1;}
  .lob-row .st{grid-column:2;justify-self:start;}
  .lob-row .lob-ping{grid-column:3;justify-self:end;}
  .lob-row .lob-join{grid-column:1 / -1;width:100%;}
}

/* ---------------------------- ficha de sala ------------------------------ */
.lob-room h3{display:flex;align-items:center;gap:var(--sp-2);margin:0 0 var(--sp-1);
  font-family:var(--tb-font);font-style:italic;text-transform:uppercase;
  font-size:var(--fs-lg);letter-spacing:0.06em;font-weight:900;}
.lob-room .rmeta{font-family:var(--tb-font-mono);font-size:var(--fs-micro);letter-spacing:0.16em;
  color:var(--tb-muted);margin-bottom:var(--sp-4);}
.lob-room .rmeta b{color:var(--tb-text);}
.lob-slots{display:grid;grid-template-columns:1fr 1fr;gap:var(--sp-3);margin-bottom:var(--sp-4);}
.lob-slot{display:flex;align-items:center;gap:var(--sp-2);padding:var(--sp-4);min-height:4rem;
  background:linear-gradient(158deg,rgba(19,27,36,0.82),rgba(8,12,18,0.82));
  font-size:var(--fs-sm);font-weight:800;letter-spacing:0.06em;}
.lob-slot .dot{width:0.6rem;height:0.6rem;background:var(--tb-cyan);
  clip-path:polygon(50% 0,100% 50%,50% 100%,0 50%);box-shadow:var(--tb-glow-cyan);}
.lob-slot.empty{color:var(--tb-muted);}
.lob-slot.empty .dot{background:var(--tb-muted);box-shadow:none;}
.lob-note{font-size:var(--fs-xs);letter-spacing:0.04em;color:var(--tb-warn);line-height:1.6;
  background:rgba(255,201,60,0.08);border-left:0.2rem solid var(--tb-warn);padding:var(--sp-3) var(--sp-4);}
.lob-err{font-size:var(--fs-xs);letter-spacing:0.04em;color:var(--tb-magenta);
  background:rgba(255,46,99,0.1);border-left:0.2rem solid var(--tb-magenta);
  padding:var(--sp-3) var(--sp-4);margin-bottom:var(--sp-4);max-width:24rem;}

/* ------------------------------ formulario ------------------------------- */
.lob-create h3{font-family:var(--tb-font);font-style:italic;text-transform:uppercase;
  font-size:var(--fs-lg);letter-spacing:0.06em;font-weight:900;margin:0 0 var(--sp-4);}
.lob-fields{display:grid;grid-template-columns:1fr;gap:var(--sp-4);}
@media (min-width:768px){.lob-fields{grid-template-columns:1fr 1fr;}}
.lob-field{display:flex;flex-direction:column;gap:var(--sp-2);text-align:left;min-width:0;}
.lob-field>span{font-family:var(--tb-font-mono);font-size:var(--fs-micro);letter-spacing:0.18em;
  color:var(--tb-muted);text-transform:uppercase;}
.lob-chk-lg{font-size:var(--fs-xs);color:var(--tb-text);margin-top:var(--sp-3);}
`;

function injectLobbyStyle(): void {
  injectThemeTokens();
  if (document.getElementById("lob-style")) return;
  const s = document.createElement("style");
  s.id = "lob-style";
  s.textContent = LOBBY_CSS;
  document.head.appendChild(s);
}

export class LobbyUI {
  private root: HTMLDivElement;
  private identity: PlayerIdentity;
  private filter: RoomFilter = { hideFull: false };
  private conn: ConnectionState = "idle";
  /**
   * Último ping medido por región. Se rellena al explorar salas y se reutiliza
   * para proponer dónde hospedar una sala propia (ver `nearestRegion`): medirlo
   * otra vez al abrir el formulario sería repetir trabajo hecho hace segundos.
   */
  private pings: RegionPing[] = [];

  /** Partida rápida: el servidor empareja (o da bot por timeout). */
  onQuickMatch: (() => void) | null = null;
  /** Unirse a una sala concreta (con contraseña si la tiene). `region` decide el
   *  edge (wss://) al que conectarse cuando hay hosting multi-región (TAREA 3). */
  onJoinRoom: ((roomId: string, password: string | null, region: RegionId) => void) | null = null;
  /** Crear una sala propia. La cancha/reglas van por defecto (se ajustan en pausa). */
  onCreateRoom: ((opts: { name: string; password: string; listed: boolean; region: string; capacity: number }) => void) | null = null;
  onExit: (() => void) | null = null;

  /** Mensaje de error a mostrar en la pantalla de entrada (p.ej. rechazo). */
  private errorMsg: string | null = null;
  /** Contraseña tecleada al unirse a una sala protegida (persiste entre renders). */
  private pendingPassword = "";

  constructor(private readonly provider: RoomListProvider) {
    injectLobbyStyle();
    this.identity = makeGuestIdentity();
    this.root = el("div", "lob-root");
    document.body.appendChild(this.root);
  }

  /** Nombre visible del jugador (elegido en la pantalla de entrada). */
  setPlayerName(name: string): void {
    this.identity = { ...this.identity, displayName: name, isGuest: true };
  }

  /** ¿Está tapando la pantalla? Lo consulta el bucle de render para no dibujar
   *  un canvas que nadie ve (ver la nota de `covered` en main.ts). */
  get isOpen(): boolean {
    return this.root.classList.contains("open");
  }

  open(): void {
    this.errorMsg = null;
    this.conn = "idle";
    this.root.classList.add("open");
    this.renderEntry();
  }

  /** Reabre el lobby mostrando un mensaje de error (p.ej. unión rechazada). */
  openWithError(msg: string): void {
    this.errorMsg = msg;
    this.conn = "server-error";
    this.root.classList.add("open");
    this.renderEntry();
  }

  close(): void {
    this.root.classList.remove("open");
    this.root.innerHTML = "";
  }

  private shell(subtitle: string, body: HTMLElement, foot: HTMLElement[]): void {
    this.root.innerHTML = "";
    const panel = el("div", "lob-panel tb-panel tb-cut tb-edge");
    const head = el("div", "lob-head tb-head");
    head.appendChild(el("div", "lob-title", `JUGAR ONLINE<small>${subtitle}</small>`));
    // Solo se enseña bandera si el país lo sabe alguien de verdad: el cliente no
    // se lo inventa (antes salía siempre 🇦🇷 hardcodeado).
    const flag = this.identity.country ? `${countryToFlag(this.identity.country)} ` : "";
    const id = el("div", "lob-id",
      `<b>${flag}${this.identity.displayName}</b><span>${this.identity.isGuest ? "INVITADO" : "CUENTA"}</span>`);
    head.appendChild(id);
    head.appendChild(el("div", `lob-conn ${this.conn}`, `<span>${this.conn.toUpperCase()}</span>`));
    panel.appendChild(head);

    const bodyWrap = el("div", "lob-body tb-scroll");
    bodyWrap.appendChild(body);
    panel.appendChild(bodyWrap);

    const f = el("div", "lob-foot tb-foot");
    for (const b of foot) f.appendChild(b);
    panel.appendChild(f);
    this.root.appendChild(panel);
  }

  /* ---------------- ENTRY ---------------- */
  private renderEntry(): void {
    const body = el("div", "lob-entry");
    if (this.identity.country) body.appendChild(el("div", "flag", countryToFlag(this.identity.country)));
    body.appendChild(el("h2", "", this.identity.displayName));
    body.appendChild(el("div", "sub", "JUGANDO COMO INVITADO · SIN REGISTRO"));

    if (this.errorMsg) {
      body.appendChild(el("div", "lob-err tb-cut-sm", `⚠ ${this.errorMsg}`));
      this.errorMsg = null;
    }
    this.conn = "idle";

    const quick = el("button", "tb-btn tb-cut-sm primary", "⚡ PARTIDA RÁPIDA");
    quick.onclick = () => void this.quickMatch();
    const browse = el("button", "tb-btn tb-cut-sm accent", "🌐 EXPLORAR SALAS");
    browse.onclick = () => void this.renderBrowser();
    const create = el("button", "tb-btn tb-cut-sm", "➕ CREAR SALA");
    create.onclick = () => this.renderCreate();
    body.appendChild(quick);
    body.appendChild(browse);
    body.appendChild(create);

    const back = el("button", "tb-btn tb-cut-sm ghost", "◀ VOLVER AL MENÚ");
    back.onclick = () => { this.close(); this.onExit?.(); };
    this.shell("ENTRADA · FRICCIÓN CERO", body, [back]);
  }

  /**
   * Región con el mejor ping medido desde este dispositivo (o la primera
   * disponible si aún no hay medidas).
   *
   * Es lo más cerca que se puede estar de "dónde estás" sin pedir permiso de
   * ubicación ni mirar la IP: el ping ya lo mide el lobby para ordenar la lista,
   * y lo que de verdad importa para hospedar no es el país, es el retardo.
   */
  private nearestRegion(): RegionId {
    let best: RegionId = AVAILABLE_REGIONS[0];
    let bestMs = Infinity;
    for (const r of AVAILABLE_REGIONS) {
      const ms = this.pingOf(r);
      if (ms == null) continue;
      if (ms < bestMs) { bestMs = ms; best = r; }
    }
    return best;
  }

  /** Ping medido de una región (o null si aún no se ha medido). */
  private pingOf(r: RegionId): number | null {
    const hit = this.pings.find((p) => p.region === r);
    return hit ? hit.rttMs : null;
  }

  /* ---------------- CREATE ROOM ---------------- */
  private renderCreate(): void {
    this.conn = "idle";
    const body = el("div", "lob-create");
    body.appendChild(el("h3", "", "CREAR SALA"));
    // Los cuatro campos van en una rejilla: una columna en móvil, dos en
    // escritorio. Lo decide el CSS (.lob-fields), no este código.
    const fields = el("div", "lob-fields");
    body.appendChild(fields);

    const nameField = el("label", "lob-field");
    nameField.appendChild(el("span", "", "Nombre de la sala"));
    const name = el("input", "tb-input");
    name.type = "text";
    name.maxLength = 24;
    name.placeholder = `Sala de ${this.identity.displayName}`;
    nameField.appendChild(name);
    fields.appendChild(nameField);

    const pwField = el("label", "lob-field");
    pwField.appendChild(el("span", "", "Contraseña (opcional)"));
    const pw = el("input", "tb-input");
    pw.type = "password";
    pw.maxLength = 32;
    pw.placeholder = "sin contraseña";
    pwField.appendChild(pw);
    fields.appendChild(pwField);

    // DÓNDE se hospeda la sala. Es la decisión que más determina cómo se juega:
    // el partido lo simula el edge de esa región, así que quien esté cerca de él
    // juega fino y quien esté al otro lado del mundo, no. Por eso la opción
    // arranca en la región de MENOR PING MEDIDO desde este dispositivo — que es
    // la manera honesta de decir "la tuya" sin preguntar dónde vives ni mirar su
    // IP. Es el equivalente al "monta tu sala" de Haxball con esta
    // infraestructura: no hay servidor casero, pero sí sala propia en el edge de
    // al lado, y el partido se anuncia como NO OFICIAL.
    const regionField = el("label", "lob-field");
    regionField.appendChild(el("span", "", "Dónde se hospeda"));
    const region = el("select", "tb-input");
    for (const r of AVAILABLE_REGIONS) {
      const ms = this.pingOf(r);
      region.appendChild(new Option(
        ms == null ? regionLabel(r) : `${regionLabel(r)} · ${ms} ms`,
        r,
      ));
    }
    region.value = this.nearestRegion();
    regionField.appendChild(region);
    fields.appendChild(regionField);

    // Capacidad de la sala (nº de jugadores). El admin reparte los equipos
    // dentro. Por defecto 8, y de 2 a 30: los slots que no se ocupen no ponen
    // discos en la cancha (ver `setSlotActive` en @tak/sim), así que abrir una
    // sala grande no cuesta nada si al final entran cuatro.
    const capField = el("label", "lob-field");
    capField.appendChild(el("span", "", "Máximo de jugadores"));
    const capacity = el("select", "tb-input");
    for (let n = MIN_CAPACITY; n <= MAX_CAPACITY; n++) {
      capacity.appendChild(new Option(`${n} jugadores`, String(n)));
    }
    capacity.value = String(DEFAULT_CAPACITY);
    capField.appendChild(capacity);
    fields.appendChild(capField);

    const listedField = el("label", "lob-chk lob-chk-lg");
    const listed = el("input");
    listed.type = "checkbox";
    listed.checked = true;
    listedField.appendChild(listed);
    listedField.appendChild(document.createTextNode("Mostrar en la lista de salas"));
    body.appendChild(listedField);

    body.appendChild(el("div", "lob-note tb-cut-sm",
      "Esto crea un PARTIDO NO OFICIAL: lo montas tú, en la región que elijas, y "
      + "entra quien tenga el enlace o lo vea en la lista. La cancha y las reglas "
      + "se ajustan en el menú de pausa (⏸) una vez dentro."));

    const back = el("button", "tb-btn tb-cut-sm ghost", "◀ ATRÁS");
    back.onclick = () => this.renderEntry();
    const submit = el("button", "tb-btn tb-cut-sm primary", "➕ CREAR Y ENTRAR");
    submit.onclick = () => {
      const trimmed = name.value.trim();
      this.close();
      this.onCreateRoom?.({
        name: trimmed || name.placeholder,
        password: pw.value,
        listed: listed.checked,
        region: region.value,
        capacity: clampCapacity(Number(capacity.value)),
      });
    };
    this.shell("CREAR SALA", body, [back, submit]);
  }

  /* ---------------- BROWSER ---------------- */
  private async renderBrowser(): Promise<void> {
    this.conn = "matchmaking";
    const body = el("div");

    const tools = el("div", "lob-tools");
    const regionSel = el("select", "tb-input lob-select");
    regionSel.appendChild(new Option("Todas las regiones", ""));
    for (const r of AVAILABLE_REGIONS) regionSel.appendChild(new Option(regionLabel(r), r));
    regionSel.value = this.filter.region ?? "";
    regionSel.onchange = () => {
      this.filter.region = (regionSel.value || undefined) as RegionId | undefined;
      void this.renderBrowser();
    };
    tools.appendChild(regionSel);

    // MM-1 · Filtro por cancha: sirve desde que la lista publica el mapa REAL
    // de cada sala (antes salía fijo como "classic" y este filtro no encontraba
    // nada que no fuese esa).
    const mapSel = el("select", "tb-input lob-select");
    mapSel.appendChild(new Option("Cualquier cancha", ""));
    for (const m of MAPS) mapSel.appendChild(new Option(m.name, m.id));
    mapSel.value = this.filter.mapId ?? "";
    mapSel.onchange = () => {
      this.filter.mapId = mapSel.value || undefined;
      void this.renderBrowser();
    };
    tools.appendChild(mapSel);

    // Tipo de partido: oficiales (emparejamiento) o amistosos (los que monta la
    // gente). Van mezclados en la lista, pero saber cuál es cuál cambia lo que
    // esperas de la partida — y quien busca "una de las mías" no quiere ver otra cosa.
    const kindSel = el("select", "tb-input lob-select");
    kindSel.appendChild(new Option("Todos los partidos", ""));
    kindSel.appendChild(new Option("Solo no oficiales", "friendly"));
    kindSel.appendChild(new Option("Solo oficiales", "official"));
    kindSel.value = this.filter.kind ?? "";
    kindSel.onchange = () => {
      this.filter.kind = (kindSel.value || undefined) as "official" | "friendly" | undefined;
      void this.renderBrowser();
    };
    tools.appendChild(kindSel);

    const sortSel = el("select", "tb-input lob-select");
    for (const [value, label] of [["ping", "Menor ping"], ["players", "Más jugadores"], ["name", "Nombre"]]) {
      sortSel.appendChild(new Option(label, value));
    }
    sortSel.value = this.filter.sort ?? "ping";
    sortSel.onchange = () => {
      this.filter.sort = sortSel.value as RoomSort;
      void this.renderBrowser();
    };
    tools.appendChild(sortSel);

    const hideFull = el("label", "lob-chk");
    const chk = el("input");
    chk.type = "checkbox";
    chk.checked = !!this.filter.hideFull;
    chk.onchange = () => { this.filter.hideFull = chk.checked; void this.renderBrowser(); };
    hideFull.appendChild(chk);
    hideFull.appendChild(document.createTextNode("Ocultar llenas"));
    tools.appendChild(hideFull);

    const count = el("div", "lob-count", "");
    tools.appendChild(count);
    body.appendChild(tools);

    // Búsqueda por nombre. Filtra sobre lo YA descargado en vez de repintar
    // (que volvería a pedir la lista y perdería el foco a cada tecla).
    const search = el("input", "tb-input lob-search");
    search.type = "search";
    search.placeholder = "Buscar sala por nombre…";
    search.value = this.filter.query ?? "";
    body.appendChild(search);

    const listWrap = el("div", "lob-list");
    listWrap.appendChild(el("div", "lob-loading", "◌ BUSCANDO SALAS…"));
    body.appendChild(listWrap);

    const back = el("button", "tb-btn tb-cut-sm ghost", "◀ ATRÁS");
    back.onclick = () => this.renderEntry();
    const refresh = el("button", "tb-btn tb-cut-sm", "⟳ REFRESCAR");
    refresh.onclick = () => void this.renderBrowser();
    this.shell("LISTA DE SALAS", body, [back, refresh]);

    const [snapshot, pings] = await Promise.all([
      this.provider.listRooms(this.filter),
      this.provider.pingRegions(),
    ]);
    this.pings = pings;
    const fetched = annotatePing(snapshot.rooms, pings);
    this.conn = "idle";
    this.updateConnBadge();

    /** Repinta la lista aplicando búsqueda y orden sobre lo ya descargado. */
    const paint = (): void => {
      const q = normalizeSearch(this.filter.query ?? "");
      const matching = q
        ? fetched.filter((r) => normalizeSearch(r.name).includes(q))
        : fetched;
      const rooms = sortRooms(matching, this.filter.sort ?? "ping");
      count.textContent = `${rooms.length} SALA${rooms.length === 1 ? "" : "S"}`;
      listWrap.innerHTML = "";
      if (rooms.length === 0) {
        // Sin servidor configurado la lista está vacía SIEMPRE, y decir "no hay
        // salas con estos filtros" mandaría a cambiar filtros que no arreglan
        // nada. Se dice lo que pasa de verdad.
        listWrap.appendChild(el("div", "lob-empty", q
          ? "Ninguna sala se llama así."
          : hasBackend()
            ? "No hay salas abiertas. Crea una y pasa el enlace."
            : "Sin servidor de juego configurado: no hay salas online. "
              + "PARTIDA LOCAL sí funciona."));
        return;
      }
      for (const room of rooms) listWrap.appendChild(this.roomRow(room));
    };

    search.oninput = () => { this.filter.query = search.value; paint(); };
    paint();
  }

  private roomRow(room: RoomWithPing): HTMLElement {
    const row = el("div", "lob-row tb-cut-sm");
    row.appendChild(el("div", "flag", countryToFlag(room.hostCountry)));

    const name = el("div", "name");
    name.appendChild(document.createTextNode(room.name));
    if (room.hasPassword) name.appendChild(el("span", "lock", "🔒"));
    row.appendChild(name);

    const free = Math.max(0, room.capacity - room.players);
    // "NO OFICIAL" se dice en la propia fila, no en un tooltip: es la diferencia
    // entre un partido que monta el juego y uno que monta una persona.
    const kindTag = (room.kind ?? "official") === "friendly" ? " · NO OFICIAL" : "";
    row.appendChild(el("div", "meta",
      `${room.players}/${room.capacity} · ${free > 0 ? `${free} libre${free === 1 ? "" : "s"}` : "sin huecos"} · ${regionLabel(room.region)}${kindTag}`));

    const st = el("div", `st ${room.status}`, statusLabel(room.status));
    row.appendChild(st);

    const tier = pingTier(room.pingMs);
    const ping = el("div", `lob-ping ${tier}`,
      `<i></i>${room.pingMs === null ? "—" : room.pingMs + "ms"}`);
    row.appendChild(ping);

    // Se puede entrar mientras QUEDE SITIO. Antes solo se dejaba entrar a las
    // salas "esperando", así que una partida en curso con seis huecos libres
    // salía como LLENA y con el botón apagado: el jugador veía media lista
    // bloqueada sin motivo. Que la partida ya haya empezado no es estar llena.
    const joinable = free > 0 && room.status !== "full";
    const join = el("button", "lob-join tb-btn tb-cut-sm" + (joinable ? " accent" : ""), joinable ? "UNIRSE" : "LLENA");
    join.disabled = !joinable;
    if (joinable) join.onclick = () => this.renderRoom(room);
    // el ping va en su propia celda; el botón reemplaza la última columna en móvil vía grid
    row.appendChild(join);
    return row;
  }

  /* ---------------- QUICKMATCH ---------------- */
  private async quickMatch(): Promise<void> {
    // Partida rápida = atajo para jugar YA. Hoy `main.ts` la implementa creando
    // una sala PRIVADA que arranca sola (autoStart) y muestra el link de
    // invitación; NO usa el matchmaking real (`quickJoin`) del servidor. Ver el
    // informe de hallazgos (docs/AUDIT-COHERENCIA.md) para la decisión pendiente
    // de si debería emparejar de verdad. Offline, cae al loopback con bot.
    // Sin pantalla de carga ni espera: el jugador ha pedido jugar YA. La partida
    // se abre en el acto y el estado de la conexión lo cuenta el cartel del HUD
    // (UX-2) sin bloquear nada. Antes había aquí un `setTimeout` de 300 ms cuyo
    // único propósito era que se viera el estado "matchmaking" del lobby mock:
    // 300 ms de espera pura que el jugador pagaba en cada partida.
    this.conn = "matchmaking";
    this.close();
    this.onQuickMatch?.();
  }

  /* ---------------- ROOM ---------------- */
  private renderRoom(room: RoomWithPing): void {
    this.pendingPassword = ""; // nueva sala → limpia la contraseña previa
    // La ficha de la sala se pinta ya lista. La conexión de verdad no ocurre
    // hasta que el jugador pulsa ENTRAR, así que fingir aquí un handshake de
    // 450 ms era solo hacerle esperar por una animación.
    this.conn = "idle";
    this.renderRoomView(room, true);
  }

  private renderRoomView(room: RoomWithPing, connected: boolean): void {
    const body = el("div", "lob-room");
    body.appendChild(el("h3", "", `${countryToFlag(room.hostCountry)} ${room.name}`));
    const tier = pingTier(room.pingMs);
    body.appendChild(el("div", "rmeta",
      `${regionLabel(room.region)} · PING ${room.pingMs === null ? "—" : room.pingMs + "ms"} (${tier.toUpperCase()})`));

    // Ocupación REAL de la sala, tal como la publica el servidor. Antes aquí
    // había dos casillas fijas — "TÚ" y "ANFITRIÓN" — que no eran datos de
    // nada: la sala podía tener ocho personas y se pintaban dos.
    const free = Math.max(0, room.capacity - room.players);
    const slots = el("div", "lob-slots");
    slots.appendChild(el("div", "lob-slot tb-cut-sm",
      `<span class="dot"></span>${room.players} jugando`));
    slots.appendChild(el("div", "lob-slot tb-cut-sm" + (free === 0 ? " empty" : ""),
      `<span class="dot"></span>${free} hueco${free === 1 ? "" : "s"} de ${room.capacity}`));
    body.appendChild(slots);
    body.appendChild(el("div", "rmeta",
      `Entras como <b>${this.identity.displayName}</b> · ${statusLabel(room.status)}`));

    if (room.hasPassword) {
      const pwField = el("label", "lob-field");
      pwField.appendChild(el("span", "", "🔒 Esta sala tiene contraseña"));
      const pw = el("input", "tb-input");
      pw.type = "password";
      pw.maxLength = 32;
      pw.placeholder = "contraseña";
      pw.value = this.pendingPassword;
      pw.oninput = () => { this.pendingPassword = pw.value; };
      pwField.appendChild(pw);
      body.appendChild(pwField);
    }

    const leave = el("button", "tb-btn tb-cut-sm ghost", "◀ SALIR");
    leave.onclick = () => this.renderBrowser();
    const start = el("button", "tb-btn tb-cut-sm primary", connected ? "▶ INICIAR PARTIDA" : "CONECTANDO…");
    start.disabled = !connected;
    start.onclick = () => {
      this.close();
      this.onJoinRoom?.(room.id, room.hasPassword ? this.pendingPassword : null, room.region);
    };
    this.shell("SALA", body, [leave, start]);
  }

  private updateConnBadge(): void {
    const badge = this.root.querySelector(".lob-conn");
    if (badge) {
      badge.className = `lob-conn ${this.conn}`;
      badge.textContent = this.conn.toUpperCase();
    }
  }
}
